import { Alert, Button, Modal, Select, Spin, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { FC, useEffect, useState } from "react";
import { IPQuestion, IPRankingProjectRecord } from "modules/organization/models/interface";
import { RankingProjectService } from "modules/organization/services/rankingProject.service";
import { runDryRun, IDryRunResult } from "../utils/dryRunChainResolver";
import { minifyEquation } from "../../utils/equationParser";
import DryRunStepNode from "./DryRunStepNode";
import ViewFormattedEquationButton from "./ViewFormattedEquationButton";
import { mnDate } from "modules/shared/services";
import { dateFormat } from "config/commonConfig";

const { Text } = Typography;

const rankingProjectService = new RankingProjectService();

// Ephemeral, per-open UI state only (record list, chosen record, run result) — deliberately not
// in the Zustand store, since nothing outside this modal needs it and it should reset each time
// the modal is reopened.
const DryRunModal: FC<{
  open: boolean;
  onClose: () => void;
  projectSlug?: string;
  rankingProjectId?: number;
  currentRankTab: string;
  liveEquationText: string;
  ranks: string[];
  allEquations: { [rankName: string]: { display_order?: number | null } };
  savedEquations: { [rankName: string]: string };
  questions: IPQuestion[];
}> = ({
  open,
  onClose,
  projectSlug,
  rankingProjectId,
  currentRankTab,
  liveEquationText,
  ranks,
  allEquations,
  savedEquations,
  questions,
}) => {
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IPRankingProjectRecord[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [dryRunOutcome, setDryRunOutcome] = useState<IDryRunResult | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !projectSlug || !rankingProjectId) {
      return;
    }
    setIsLoadingRecords(true);
    setFetchError(null);
    rankingProjectService
      .getRankingProjectRecords(projectSlug, rankingProjectId)
      .then((response) => {
        setRecords(response.data?.data?.records ?? []);
      })
      .catch(() => {
        setFetchError("Failed to load records for this ranking project.");
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  }, [open, projectSlug, rankingProjectId]);

  useEffect(() => {
    if (!open) {
      setRecords([]);
      setSelectedRecordId(null);
      setDryRunOutcome(null);
      setFetchError(null);
    }
  }, [open]);

  const handleRun = async () => {
    if (!projectSlug || !rankingProjectId || selectedRecordId == null) {
      return;
    }
    setIsRunning(true);
    setFetchError(null);
    setDryRunOutcome(null);
    try {
      const response = await rankingProjectService.getRecordAnswers(
        projectSlug,
        rankingProjectId,
        selectedRecordId,
      );
      const answers = response.data?.data?.answers ?? [];
      const outcome = runDryRun(
        currentRankTab,
        liveEquationText,
        ranks,
        allEquations,
        savedEquations,
        questions,
        answers,
      );
      setDryRunOutcome(outcome);
    } catch (error) {
      setFetchError("Failed to fetch this record's answers.");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Modal
      className="dryRunModal"
      title={
        <span className="dryRunModalTitle">
          Rank Evaluation Explained — {currentRankTab}
          <Tooltip
            placement="right"
            overlayClassName="dryRunInfoTooltip"
            title="Pick a real record and click Run to see whether this rank's equation (including any unsaved edits) would Pass or Fail for it — with a step-by-step breakdown of every operator and variable involved. If the equation imports another rank, expand it to see that rank's own breakdown too. Nothing is saved by running this."
          >
            <InfoCircleOutlined className="dryRunModalTitleInfo" />
          </Tooltip>
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose}>Close</Button>
      }
      width="85vw"
    >
      <div className="dryRunRecordPicker">
        <Text strong className="dryRunRecordPickerLabel">
          Record:
        </Text>
        <Select
          className="dryRunRecordSelect"
          showSearch
          placeholder="Choose a record"
          loading={isLoadingRecords}
          value={selectedRecordId ?? undefined}
          onChange={(value) => setSelectedRecordId(value)}
          optionFilterProp="label"
          options={records.map((record) => ({
            value: record.record_id,
            label: `${record.record_name} | (${mnDate(record.effective_from).format(dateFormat.default)} - ${mnDate(record.through_to).format(dateFormat.default)}) | ${record.series_title}`,
          }))}
        />
        <Button type="primary" loading={isRunning} disabled={selectedRecordId == null} onClick={handleRun}>
          Run
        </Button>
      </div>

      {fetchError && <Alert type="error" showIcon message={fetchError} className="dryRunAlert" />}

      {isRunning && <Spin className="dryRunSpinner" />}

      {dryRunOutcome && !isRunning && (
        <div className="dryRunResultArea">
          {dryRunOutcome.error ? (
            <Alert type="error" showIcon message={dryRunOutcome.error} className="dryRunAlert" />
          ) : (
            <>
              <div className="dryRunEquationHeader">
                <span className="dryRunEquationHeaderLabel">
                  <Text strong>Target Equation</Text>
                  <ViewFormattedEquationButton equationText={liveEquationText} />
                </span>
                <Tag className={dryRunOutcome.result ? "dryRunResultTrue" : "dryRunResultFalse"}>
                  {dryRunOutcome.result ? "Passed" : "Failed"}
                </Tag>
              </div>
              <div className="dryRunEquationLine">
                <pre className="dryRunEquationText">{minifyEquation(liveEquationText)}</pre>
              </div>
              {dryRunOutcome.tree && <DryRunStepNode node={dryRunOutcome.tree} />}
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DryRunModal;
